import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { DataGrid, type GridColDef } from "@act-group/act-ui-library";
import { useUsers } from "../hooks/useUsers";
import { User } from "../types/user";
import { isHttpError, isValidationError } from "../../../errors/isApiError";
import { siConsoleGridSx } from "../../../theme/dataGridSx";

function UsersPage() {
  const { users, isLoading, error } = useUsers();
  const theme = useTheme();
  const { t } = useTranslation();

  // Built in-render so the headers re-translate when the language changes.
  const columns = useMemo<GridColDef<User>[]>(
    () => [
      { field: "name", headerName: t("User"), flex: 1, minWidth: 180 },
      { field: "email", headerName: t("Email"), flex: 1, minWidth: 220 },
      { field: "role", headerName: t("Role"), width: 140 },
      { field: "permissions", headerName: t("Permissions"), width: 130 },
      { field: "device", headerName: t("Device"), width: 110 },
      { field: "address", headerName: t("Location"), width: 200 },
      { field: "date", headerName: t("Invite Date"), width: 170 },
      { field: "sessions", headerName: t("Last Session"), width: 170 },
      { field: "createdAt", headerName: t("Created At"), width: 170 },
    ],
    [t]
  );

  return (
    <Stack spacing={3}>
      <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary" }}>
        {t("Users")}
      </Typography>

      {error ? (
        <Alert severity="error">
          {isHttpError(error) || isValidationError(error)
            ? error.message
            : t("Failed to load users.")}
        </Alert>
      ) : (
        <Box sx={siConsoleGridSx(theme.palette.mode)}>
          <DataGrid<User>
            columns={columns}
            rows={users}
            getRowId={(row) => row.id}
            loading={isLoading}
            paginationMode="client"
          />
        </Box>
      )}
    </Stack>
  );
}

export default UsersPage;
